import React, { useEffect, useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { adminApiService } from '../../services/adminApiService';
import {
  stockPortfolioService,
  StockInstrument,
  StockPosition,
} from '../../services/stockPortfolioService';

interface StockInstrumentDrawerProps {
  open: boolean;
  configId: number;
  /** DEMO or LIVE — shown in the open-order panel. */
  environment: string;
  instrument: StockInstrument | null;
  ticker: string;
  position: StockPosition | null;
  currency: string;
  canManage: boolean;
  onClose: () => void;
  /** Called after a successful buy/sell so the page can refresh positions. */
  onPlaced: () => void;
}

type ChartPoint = { t: number; price: number; label: string };

function displayTicker(ticker: string | null | undefined): string {
  if (!ticker) return '—';
  return ticker.split('_')[0];
}

/** MI board instrument ids for the STOCK rows on Market Intelligence. */
function miBoardId(ticker: string): string | null {
  const short = displayTicker(ticker).toLowerCase();
  return ['spy', 'qqq', 'aapl', 'msft', 'nvda', 'tsla'].includes(short) ? short : null;
}

function fmtMoney(n: number | null | undefined, currency: string): string {
  const v = Number(n ?? 0);
  if (!Number.isFinite(v)) return '—';
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(v);
  } catch {
    return `${v < 0 ? '-' : ''}${currency} ${Math.abs(v).toFixed(2)}`;
  }
}

function fmtQty(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '—';
  if (n >= 100) return n.toFixed(2);
  if (n >= 1) return n.toFixed(4);
  return n.toFixed(6);
}

const StockInstrumentDrawer: React.FC<StockInstrumentDrawerProps> = ({
  open,
  configId,
  environment,
  instrument,
  ticker,
  position,
  currency,
  canManage,
  onClose,
  onPlaced,
}) => {
  const effectiveTicker = instrument?.ticker || ticker;
  const name = instrument?.name || instrument?.shortName || '';
  const isLive = environment === 'LIVE';
  const held = position != null;
  const positionPrice = Number(position?.currentPrice ?? 0);
  const positionValue = held
    ? Number(position!.quantity ?? 0) * positionPrice
    : 0;
  const ppl = Number(position?.ppl ?? 0);

  const [side, setSide] = useState<'BUY' | 'SELL'>('BUY');
  /** When price is known: spend this much. When missing: treat as share quantity. */
  const [amount, setAmount] = useState('5');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);

  const [chartPoints, setChartPoints] = useState<ChartPoint[]>([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [quotePrice, setQuotePrice] = useState<number | null>(null);
  const [quoteChangePct, setQuoteChangePct] = useState<number | null>(null);
  const [quoteSource, setQuoteSource] = useState<string | null>(null);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [isMobileViewport, setIsMobileViewport] = useState(
    () => (typeof window !== 'undefined' ? window.matchMedia('(max-width: 768px)').matches : false),
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const onChange = () => setIsMobileViewport(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Reset trade form when the drawer opens on a new instrument.
  useEffect(() => {
    if (!open) return;
    setSide('BUY');
    setAmount('5');
    setSubmitError(null);
    setSubmitSuccess(null);
  }, [open, effectiveTicker]);

  // Price + chart: MI board/history first (same feed as Market Intelligence page),
  // then Stock Portfolio /market-data, then open-position price.
  useEffect(() => {
    if (!open || !effectiveTicker || !configId) return;
    let cancelled = false;
    const boardId = miBoardId(effectiveTicker);

    setChartLoading(true);
    setChartPoints([]);
    setQuotePrice(positionPrice > 0 ? positionPrice : null);
    setQuoteChangePct(null);
    setQuoteSource(positionPrice > 0 ? 'position' : null);
    setQuoteError(null);

    const toChartPoints = (points: Array<{ t: number; price: number }>): ChartPoint[] =>
      points
        .filter(p => p && Number(p.price) > 0 && Number(p.t) > 0)
        .map(p => ({
          t: Number(p.t),
          price: Number(p.price),
          label: new Date(Number(p.t) * 1000).toLocaleString('en-GB', {
            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
          }),
        }));

    const sparklineToPoints = (spark: number[]): ChartPoint[] => {
      const now = Math.floor(Date.now() / 1000);
      return spark
        .filter(v => Number(v) > 0)
        .map((price, idx, arr) => ({
          t: now - (arr.length - 1 - idx) * 3600,
          price: Number(price),
          label: '',
        }));
    };

    (async () => {
      let gotPrice = false;
      let gotChart = false;
      let gotChange = false;
      const errors: string[] = [];

      // 1) Market Intelligence board + history — same APIs the MI page already uses successfully.
      if (boardId) {
        try {
          const [boardRes, histRes] = await Promise.all([
            adminApiService.getMarketsBoard(),
            adminApiService.getMarketsHistory(boardId, '5d'),
          ]);
          if (cancelled) return;

          if (boardRes.success && boardRes.data) {
            const row = (boardRes.data.instruments || []).find(
              (i: any) =>
                String(i.id).toLowerCase() === boardId ||
                String(i.symbol).toUpperCase() === displayTicker(effectiveTicker).toUpperCase(),
            );
            if (row?.price != null && Number(row.price) > 0) {
              setQuotePrice(Number(row.price));
              setQuoteSource(row.source || 'market-intelligence');
              gotPrice = true;
            }
            if (row?.changePercent != null) {
              setQuoteChangePct(Number(row.changePercent));
              gotChange = true;
            }
            if (!gotChart && Array.isArray(row?.sparkline) && row.sparkline.length > 1) {
              setChartPoints(sparklineToPoints(row.sparkline));
              gotChart = true;
            }
          } else if (boardRes.error) {
            errors.push(String(boardRes.error));
          }

          if (histRes.success && Array.isArray(histRes.data?.points) && histRes.data.points.length > 0) {
            setChartPoints(toChartPoints(histRes.data.points));
            gotChart = true;
            if (!gotPrice) {
              const last = histRes.data.points[histRes.data.points.length - 1];
              if (last?.price != null && Number(last.price) > 0) {
                setQuotePrice(Number(last.price));
                setQuoteSource(histRes.data.source || 'market-intelligence');
                gotPrice = true;
              }
            }
          }
        } catch (e: any) {
          errors.push(e?.message || 'Market Intelligence feed failed');
        }
      }

      // 2) Dedicated stock-portfolio market-data endpoint (Yahoo for any ticker).
      if (!cancelled && (!gotPrice || !gotChart)) {
        try {
          const data = await stockPortfolioService.getMarketData(configId, effectiveTicker, '5d');
          if (cancelled) return;
          if (!gotPrice && data.price != null && Number(data.price) > 0) {
            setQuotePrice(Number(data.price));
            setQuoteSource(data.source || 'yahoo');
            gotPrice = true;
          }
          if (!gotChange && data.changePercent != null) {
            setQuoteChangePct(Number(data.changePercent));
            gotChange = true;
          }
          if (!gotChart && Array.isArray(data.points) && data.points.length > 0) {
            setChartPoints(toChartPoints(data.points));
            gotChart = true;
          }
        } catch (e: any) {
          errors.push(e?.message || 'Stock market-data failed');
        }
      }

      if (!cancelled) {
        if (!gotPrice && positionPrice > 0) {
          setQuotePrice(positionPrice);
          setQuoteSource('position');
          gotPrice = true;
        }
        if (!gotPrice && errors.length > 0) {
          setQuoteError(errors[0]);
        }
        setChartLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, effectiveTicker, configId, positionPrice]);

  const price = useMemo(() => {
    if (quotePrice != null && quotePrice > 0) return quotePrice;
    if (positionPrice > 0) return positionPrice;
    if (chartPoints.length > 0) {
      const last = chartPoints[chartPoints.length - 1].price;
      if (last > 0) return last;
    }
    return null;
  }, [quotePrice, positionPrice, chartPoints]);

  const tradeCurrency = useMemo(() => {
    if (instrument?.currencyCode === 'GBX') return 'GBP';
    if (instrument?.currencyCode) return instrument.currencyCode;
    const short = displayTicker(effectiveTicker).toUpperCase();
    if (['SPY', 'QQQ', 'AAPL', 'MSFT', 'NVDA', 'TSLA'].includes(short)) return 'USD';
    return currency;
  }, [instrument?.currencyCode, effectiveTicker, currency]);

  const amountNum = Number(amount);
  /** true = amount is cash; false = amount is share quantity (no price available). */
  const amountIsCash = price != null;
  const estimatedQty = useMemo(() => {
    if (!Number.isFinite(amountNum) || amountNum <= 0) return null;
    if (!amountIsCash) return amountNum;
    return amountNum / (price as number);
  }, [amountNum, amountIsCash, price]);

  const minQty = instrument?.minTradeQuantity != null ? Number(instrument.minTradeQuantity) : null;
  const heldQty = held ? Number(position!.quantity ?? 0) : 0;

  const validationError = useMemo(() => {
    if (!canManage) return null;
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      return amountIsCash ? 'Enter an amount greater than zero' : 'Enter a share quantity greater than zero';
    }
    if (estimatedQty == null || estimatedQty <= 0) return 'Could not estimate share quantity';
    if (minQty != null && estimatedQty < minQty) {
      return amountIsCash && price != null
        ? `Amount too small — min trade is ${minQty} shares (~${fmtMoney(minQty * price, tradeCurrency)})`
        : `Min trade quantity is ${minQty} shares`;
    }
    if (side === 'SELL' && estimatedQty > heldQty + 1e-9) {
      return `You only hold ${heldQty} shares`;
    }
    if (side === 'SELL' && !held) return 'You do not hold this instrument';
    return null;
  }, [canManage, amountNum, amountIsCash, estimatedQty, minQty, tradeCurrency, price, side, heldQty, held]);

  const chartHigh = chartPoints.length ? Math.max(...chartPoints.map(p => p.price)) : null;
  const chartLow = chartPoints.length ? Math.min(...chartPoints.map(p => p.price)) : null;
  const chartUp = (quoteChangePct ?? 0) >= 0;

  const placeOrder = async () => {
    if (validationError || estimatedQty == null || !effectiveTicker) return;
    setSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);
    try {
      // Round down slightly so we don't overshoot a sell of the full holding.
      let qty = estimatedQty;
      if (side === 'SELL' && held) {
        qty = Math.min(qty, heldQty);
      }
      // Trading 212 rejects quantities with more than ~3 decimal places (0.01625 fails).
      qty = Math.floor(qty * 1000) / 1000;
      if (qty <= 0) {
        throw new Error('Quantity rounds to zero at Trading 212 precision — increase the amount (try at least ~$1–2 more)');
      }

      await stockPortfolioService.placeStockOrder(configId, {
        ticker: effectiveTicker,
        side,
        type: 'MARKET',
        quantity: qty,
      });
      setSubmitSuccess(
        amountIsCash
          ? `${side === 'BUY' ? 'Bought' : 'Sold'} ~${fmtQty(qty)} ${displayTicker(effectiveTicker)} for about ${fmtMoney(amountNum, tradeCurrency)}`
          : `${side === 'BUY' ? 'Bought' : 'Sold'} ${fmtQty(qty)} ${displayTicker(effectiveTicker)} shares`,
      );
      onPlaced();
    } catch (e: any) {
      setSubmitError(e?.message || 'Order failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  // Above admin mobile bottom nav (z-index 9999) so Buy/Sell isn't covered.
  const overlayZ = 10040;
  const panelZ = 10041;

  return (
    <>
      <div
        role="presentation"
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.45)',
          zIndex: overlayZ,
        }}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="stock-instrument-drawer-title"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          left: isMobileViewport ? 0 : 'auto',
          height: isMobileViewport ? '100dvh' : '100%',
          maxHeight: isMobileViewport ? '100dvh' : undefined,
          width: isMobileViewport ? '100%' : 'min(420px, 100vw)',
          background: '#fff',
          boxShadow: '-8px 0 32px rgba(0,0,0,0.12)',
          zIndex: panelZ,
          display: 'flex',
          flexDirection: 'column',
          fontFamily: 'Nunito, system-ui, sans-serif',
          boxSizing: 'border-box',
        }}
      >
        {/* Header — mirrors Market Intelligence drawer */}
        <div
          style={{
            padding: '20px 20px 16px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div
              id="stock-instrument-drawer-title"
              style={{ fontSize: 18, fontWeight: 800, color: '#1e293b' }}
            >
              {displayTicker(effectiveTicker)}
            </div>
            {name && (
              <div style={{ fontSize: 14, fontWeight: 600, color: '#475569', marginTop: 4, lineHeight: 1.35 }}>
                {name}
              </div>
            )}
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
              {[instrument?.type || 'Equity', 'Trading 212'].filter(Boolean).join(' · ')}
            </div>
            <div
              style={{
                fontSize: 11,
                color: '#cbd5e1',
                marginTop: 2,
                fontFamily: 'Roboto Mono, ui-monospace, monospace',
              }}
            >
              {effectiveTicker}
            </div>
            <div
              style={{
                fontSize: 26,
                fontWeight: 800,
                color: '#0f172a',
                marginTop: 10,
                letterSpacing: '-0.02em',
                lineHeight: 1.1,
              }}
            >
              {price != null ? fmtMoney(price, tradeCurrency) : '—'}
            </div>
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 8 }}>
              {held
                ? `Open position · ${Number(position!.quantity ?? 0)} shares`
                : 'No open position yet'}
            </div>
            {(quoteChangePct != null || isLive || !isLive) && (
              <div
                style={{
                  fontSize: 12,
                  marginTop: 6,
                  color: quoteChangePct != null ? (chartUp ? '#15803d' : '#b91c1c') : '#94a3b8',
                  fontWeight: 600,
                }}
              >
                {quoteChangePct != null && (
                  <>
                    {chartUp ? '+' : ''}
                    {quoteChangePct.toFixed(2)}%
                  </>
                )}
                <span style={{ color: '#94a3b8', fontWeight: 500, marginLeft: quoteChangePct != null ? 8 : 0 }}>
                  {isLive ? 'LIVE' : 'DEMO'}
                </span>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              border: 'none',
              background: '#f1f5f9',
              borderRadius: 8,
              width: 32,
              height: 32,
              cursor: 'pointer',
              fontSize: 18,
              lineHeight: 1,
              color: '#475569',
              flexShrink: 0,
            }}
          >
            ×
          </button>
        </div>

        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            padding: '16px 20px',
            paddingBottom: 'calc(28px + env(safe-area-inset-bottom, 0px))',
            minHeight: 0,
          }}
        >
          {/* Chart */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b' }}>Price activity</div>
              {chartHigh != null && chartLow != null && (
                <div style={{ fontSize: 11, color: '#94a3b8' }}>
                  H {fmtMoney(chartHigh, tradeCurrency)} · L {fmtMoney(chartLow, tradeCurrency)}
                </div>
              )}
            </div>
            <div
              style={{
                height: 140,
                borderRadius: 10,
                border: '1px solid #e2e8f0',
                background: '#f8fafc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {chartLoading && (
                <span style={{ fontSize: 12, color: '#94a3b8' }}>Loading chart…</span>
              )}
              {!chartLoading && chartPoints.length === 0 && (
                <span style={{ fontSize: 12, color: '#94a3b8', padding: 12, textAlign: 'center' }}>
                  {quoteError
                    ? `Chart unavailable: ${quoteError}`
                    : held
                      ? 'No history for this ticker — price from your open position.'
                      : 'No chart available for this instrument.'}
                </span>
              )}
              {!chartLoading && chartPoints.length > 0 && (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartPoints} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="stockFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={chartUp ? '#0dca9d' : '#ff7171'} stopOpacity={0.35} />
                        <stop offset="100%" stopColor={chartUp ? '#0dca9d' : '#ff7171'} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="label" hide />
                    <YAxis
                      domain={['auto', 'auto']}
                      width={48}
                      tick={{ fontSize: 10, fill: '#94a3b8' }}
                      tickFormatter={(v: number) => (v >= 100 ? v.toFixed(0) : v.toFixed(2))}
                    />
                    <Tooltip
                      formatter={(v: number) => [fmtMoney(v, tradeCurrency), 'Price']}
                      labelStyle={{ fontSize: 11 }}
                      contentStyle={{ fontSize: 12, borderRadius: 8 }}
                    />
                    <Area
                      type="monotone"
                      dataKey="price"
                      stroke={chartUp ? '#0dca9d' : '#ff7171'}
                      fill="url(#stockFill)"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>
              Chart/price from {quoteSource || 'Yahoo / Market Intelligence'}. Trading 212 fill price may differ slightly.
            </div>
          </div>

          {/* Open order panel */}
          {canManage && (
            <div
              style={{
                border: '2px solid #086afb',
                borderRadius: 12,
                padding: '14px 16px',
                marginBottom: 16,
                background: 'linear-gradient(180deg, #f0f7ff 0%, #f8fafc 100%)',
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b', marginBottom: 6 }}>
                Open new order
              </div>
              <p style={{ margin: '0 0 12px', fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>
                Market order on your Trading 212 {isLive ? 'LIVE' : 'demo'} account.
                {amountIsCash
                  ? ' Enter how much to spend — we convert it to shares using the latest known price.'
                  : ' No live price for this ticker yet — enter the number of shares to buy instead.'}
              </p>

              {isLive && (
                <div
                  role="alert"
                  style={{
                    padding: '8px 10px',
                    borderRadius: 8,
                    marginBottom: 12,
                    fontSize: 12,
                    fontWeight: 700,
                    background: '#fee2e2',
                    border: '1px solid #fca5a5',
                    color: '#b91c1c',
                  }}
                >
                  LIVE account — real money.
                </div>
              )}

              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 6 }}>
                Side
              </label>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                {(['BUY', 'SELL'] as const).map(s => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => {
                      setSide(s);
                      setSubmitError(null);
                      setSubmitSuccess(null);
                    }}
                    disabled={s === 'SELL' && !held}
                    style={{
                      flex: 1,
                      padding: '8px 0',
                      borderRadius: 8,
                      border: '1px solid',
                      borderColor:
                        side === s ? (s === 'BUY' ? '#086afb' : '#b91c1c') : '#cbd5e1',
                      background:
                        side === s ? (s === 'BUY' ? '#086afb' : '#fee2e2') : '#fff',
                      color: side === s ? (s === 'BUY' ? '#fff' : '#b91c1c') : '#64748b',
                      fontWeight: 800,
                      fontSize: 13,
                      cursor: s === 'SELL' && !held ? 'not-allowed' : 'pointer',
                      opacity: s === 'SELL' && !held ? 0.45 : 1,
                    }}
                  >
                    {s}
                  </button>
                ))}
              </div>

              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 4 }}>
                {amountIsCash ? `Amount (${tradeCurrency})` : 'Shares'}
              </label>
              <input
                type="number"
                min="0"
                step="any"
                value={amount}
                onChange={e => {
                  setAmount(e.target.value);
                  setSubmitError(null);
                  setSubmitSuccess(null);
                }}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  borderRadius: 8,
                  border: '1px solid #e2e8f0',
                  fontSize: 13,
                  marginBottom: 8,
                  boxSizing: 'border-box',
                }}
              />
              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 12, lineHeight: 1.5 }}>
                {amountIsCash
                  ? `≈ ${fmtQty(estimatedQty ?? 0)} shares @ ${fmtMoney(price, tradeCurrency)}`
                  : `${fmtQty(estimatedQty ?? 0)} shares (price unavailable)`}
                {held ? ` · you hold ${heldQty}` : ''}
                {amountIsCash && currency !== tradeCurrency ? ` · account cash is ${currency}` : ''}
              </div>

              {submitSuccess && (
                <div
                  role="status"
                  style={{
                    padding: '10px 12px',
                    background: '#f0fdf4',
                    border: '1px solid #bbf7d0',
                    borderRadius: 8,
                    color: '#15803d',
                    fontSize: 12,
                    marginBottom: 10,
                  }}
                >
                  {submitSuccess}
                </div>
              )}
              {(submitError || validationError) && (
                <div role="alert" style={{ fontSize: 12, color: '#b91c1c', marginBottom: 10 }}>
                  {submitError || validationError}
                </div>
              )}

              <button
                type="button"
                onClick={() => void placeOrder()}
                disabled={submitting || !!validationError}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 999,
                  border: 'none',
                  background: submitting || validationError
                    ? '#94a3b8'
                    : side === 'BUY'
                      ? '#e7038e'
                      : '#b91c1c',
                  color: '#fff',
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: submitting || validationError ? 'not-allowed' : 'pointer',
                }}
              >
                {submitting
                  ? 'Placing order…'
                  : amountIsCash
                    ? `${side === 'BUY' ? 'Buy' : 'Sell'} ${displayTicker(effectiveTicker)} · ${fmtMoney(amountNum || 0, tradeCurrency)}${isLive ? ' (LIVE)' : ''}`
                    : `${side === 'BUY' ? 'Buy' : 'Sell'} ${fmtQty(amountNum || 0)} ${displayTicker(effectiveTicker)}${isLive ? ' (LIVE)' : ''}`}
              </button>
            </div>
          )}

          {!canManage && (
            <p style={{ fontSize: 12, color: '#94a3b8', marginBottom: 16 }}>
              Trading requires the MANAGE_STOCK_PORTFOLIO permission.
            </p>
          )}

          {/* Held position summary */}
          {held && (
            <div
              style={{
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: 10,
                padding: '12px 14px',
                marginBottom: 12,
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>
                Your open position
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ color: '#64748b' }}>Quantity</span>
                <span style={{ fontWeight: 700 }}>{heldQty}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ color: '#64748b' }}>Value</span>
                <span style={{ fontWeight: 700 }}>{fmtMoney(positionValue, currency)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '6px 0' }}>
                <span style={{ color: '#64748b' }}>Unrealized P&L</span>
                <span style={{ fontWeight: 700, color: ppl >= 0 ? '#15803d' : '#b91c1c' }}>
                  {ppl >= 0 ? '+' : ''}{fmtMoney(ppl, currency)}
                </span>
              </div>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default StockInstrumentDrawer;
